// The showcase of the controls (DESIGN.md «Контролы»): every control of the system in every state, in the current theme — a hidden
// page of the account's owner (/s/ui-kit). Also the page the browser tests of the controls run on (tools/e2e/ui-kit.mjs).

import { useState, type ReactNode } from 'react';
import { useVariant } from '../shell/variants.tsx';
import { DateInput } from './DateInput.tsx';
import { Page, PageBlock } from './Page.tsx';
import {
  Button, Checkbox, Chevron, Chip, Count, Dialog, Field, IconButton, Input, Menu, MenuCheck, MenuContent, MenuItem, MenuLabel, MenuSeparator,
  MenuSub, MenuSubContent, MenuSubTrigger, MenuTrigger, RadioGroup, SearchField, Segmented, Select, Switch, Tabs, TextArea, useToast,
} from './primitives.tsx';
import { Tooltip } from './Tooltip.tsx';
import s from './ui-kit.module.css';

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className={s.row}><span className={s.label}>{label}</span><div className={s.cells}>{children}</div></div>
);

const CITIES = ['Москва', 'Санкт-Петербург', 'Казань', 'Екатеринбург', 'Новосибирск', 'Нижний Новгород', 'Самара', 'Ростов-на-Дону', 'Краснодар', 'Пермь', 'Воронеж', 'Уфа']
  .map((c) => ({ value: c, label: c }));

export function UiKit() {
  const toast = useToast();
  const [theme, setTheme] = useVariant('theme');
  const [checks, setChecks] = useState([true, false, false]);
  const [radio, setRadio] = useState<'all' | 'mine' | 'none' | 'off'>('mine');
  const [on, setOn] = useState(true);
  const [seg, setSeg] = useState<'none' | 'read' | 'write'>('read');
  const [view, setView] = useState<'table' | 'grid'>('table');
  const [tab, setTab] = useState<'one' | 'two' | 'three' | 'more'>('one');
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(12);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState('read');
  const [city, setCity] = useState('');
  const [date, setDate] = useState('');
  const [dialog, setDialog] = useState(false);
  const [menuChecks, setMenuChecks] = useState({ title: true, stage: false });
  const all = checks.every(Boolean) ? true : checks.some(Boolean) ? 'mixed' : false;

  return (
    <Page title="Контролы" sub="Все контролы системы во всех состояниях. Наведите, нажмите, пройдите клавиатурой — Tab, пробел, стрелки."
      end={<Segmented label="Тема" value={theme} onChange={setTheme} items={[{ value: 'light', label: 'Светлая', icon: 'sun' }, { value: 'dark', label: 'Тёмная', icon: 'moon' }, { value: 'system', label: 'Как в системе', icon: 'monitor' }]} />}>
      <div className={s.kit} data-testid="ui-kit">
        <PageBlock title="Кнопки" help="Варианты и три высоты: 26, 30, 36. Нажатие — масштаб 0,98; кнопка меню — стрелка поворачивается, пока меню открыто.">
          {(['primary', 'secondary', 'ghost', 'danger'] as const).map((v) => (
            <Row key={v} label={v}>
              <Button variant={v} size="sm" icon="plus">Малая</Button>
              <Button variant={v} icon="plus">Обычная</Button>
              <Button variant={v} size="lg">Крупная</Button>
              <Button variant={v} disabled>Выключена</Button>
            </Row>
          ))}
          <Row label="Кнопка-иконка">
            <IconButton size="sm" icon="edit" label="Переименовать" />
            <IconButton icon="trash" label="Удалить" hint="Удалить (Del)" />
            <IconButton icon="star" label="Важное" pressed />
            <IconButton icon="close" label="Закрыть" disabled />
          </Row>
          <Row label="Открывает меню">
            <Menu>
              <MenuTrigger asChild><Button variant="secondary" caret data-testid="menu-trigger">Действия</Button></MenuTrigger>
              <MenuContent>
                <MenuLabel>Запись</MenuLabel>
                <MenuItem icon="edit" shortcut={['mod', 'E']}>Переименовать</MenuItem>
                <MenuItem icon="copy">Дублировать</MenuItem>
                <MenuCheck checked={menuChecks.title} onChange={(v) => setMenuChecks({ ...menuChecks, title: v })}>Показывать название</MenuCheck>
                <MenuCheck checked={menuChecks.stage} onChange={(v) => setMenuChecks({ ...menuChecks, stage: v })}>Показывать этап</MenuCheck>
                <MenuSub>
                  <MenuSubTrigger icon="board">Раскладка</MenuSubTrigger>
                  <MenuSubContent><MenuItem icon="table">Таблица</MenuItem><MenuItem icon="board">Доска</MenuItem></MenuSubContent>
                </MenuSub>
                <MenuSeparator />
                <MenuItem icon="trash" danger>Удалить</MenuItem>
              </MenuContent>
            </Menu>
            <Menu>
              <MenuTrigger asChild><IconButton icon="more" label="Ещё" /></MenuTrigger>
              <MenuContent align="end"><MenuItem icon="download">Выгрузить</MenuItem><MenuItem icon="mail">Письмо выборке</MenuItem></MenuContent>
            </Menu>
          </Row>
        </PageBlock>

        <PageBlock title="Поля" help="Фокус — мягкое кольцо цвета акцента вокруг рамки; ошибка — красная рамка и текст под полем.">
          <Row label="Высоты">
            <Input size="sm" placeholder="Малое" aria-label="Малое поле" />
            <Input placeholder="Обычное" aria-label="Обычное поле" />
            <Input size="lg" placeholder="Крупное" aria-label="Крупное поле" />
          </Row>
          <Row label="Состояния">
            <Field label="С подсказкой" hint="Видно в карточке"><Input defaultValue="Текст" /></Field>
            <Field label="С ошибкой" error="Укажите адрес почты"><Input invalid defaultValue="ivan@" /></Field>
            <Field label="Выключено"><Input disabled defaultValue="Нельзя менять" /></Field>
          </Row>
          <Row label="Поиск и дата">
            <SearchField value={q} onChange={setQ} label="Поиск" placeholder="Найти…" slash data-testid="search" />
            <SearchField ghost size="sm" value={q} onChange={setQ} label="Поиск в строке фильтров" placeholder="Тихий поиск" />
            <DateInput value={date} onChange={setDate} aria-label="Дата" />
          </Row>
          <Row label="Текст"><TextArea placeholder="Несколько строк" aria-label="Многострочное поле" rows={3} /></Row>
        </PageBlock>

        <PageBlock title="Выбор из списка" help="Список — меню системы. Список, который растёт с данными (события, записи, люди), открывается со строкой поиска: так решает экран (search), а не число вариантов. Стрелка поворачивается, пока список открыт.">
          <Row label="Select">
            <Select label="Права" value={sel} onChange={setSel} options={[{ value: 'none', label: 'Нет' }, { value: 'read', label: 'Чтение' }, { value: 'write', label: 'Изменение' }]} />
            <Select search label="Город" value={city} onChange={setCity} options={CITIES} placeholder="Город…" />
            <Select size="sm" label="Малый" value={sel} onChange={setSel} options={[{ value: 'read', label: 'Чтение' }, { value: 'write', label: 'Изменение' }]} />
            <Select label="С ошибкой" value="" onChange={() => {}} options={[{ value: 'a', label: 'А' }]} invalid placeholder="Не выбрано" />
            <Select label="Выключен" value="read" onChange={() => {}} options={[{ value: 'read', label: 'Чтение' }]} disabled />
          </Row>
        </PageBlock>

        <PageBlock title="Галки, радио, переключатели" help="Галка рисуется штрихом, радио — кольцо с пружиной, переключатель скользит и тянется при нажатии. Переключатель — для того, что применяется сразу; галка — для формы с кнопкой «Сохранить».">
          <Row label="Checkbox">
            <Checkbox checked={all} onChange={(v) => setChecks(checks.map(() => v))} data-testid="check-all">Все</Checkbox>
            {checks.map((c, i) => <Checkbox key={i} checked={c} onChange={(v) => setChecks(checks.map((x, j) => (j === i ? v : x)))} data-testid={`check-${i}`}>Пункт {i + 1}</Checkbox>)}
            <Checkbox checked disabled>Выключена</Checkbox>
            <Checkbox checked={false} invalid>Нужно согласие</Checkbox>
          </Row>
          <Row label="Radio">
            <RadioGroup label="Кому видно" value={radio} onChange={setRadio} row options={[{ value: 'all', label: 'Всем' }, { value: 'mine', label: 'Своим', hint: 'команде события' }, { value: 'none', label: 'Никому' }, { value: 'off', label: 'Нельзя', disabled: true }]} />
          </Row>
          <Row label="Switch">
            <Switch checked={on} onChange={setOn} label="Сценарий включён">{on ? 'Включён' : 'Выключен'}</Switch>
            <Switch size="sm" checked={!on} onChange={(v) => setOn(!v)} label="Малый переключатель">Малый</Switch>
            <Switch checked disabled onChange={() => {}} label="Выключен">Нельзя менять</Switch>
          </Row>
          <Row label="Segmented">
            <Segmented label="Права" value={seg} onChange={setSeg} items={[{ value: 'none', label: 'Нет' }, { value: 'read', label: 'Чтение' }, { value: 'write', label: 'Изменение' }]} />
            <Segmented size="sm" label="Вид" value={view} onChange={setView} items={[{ value: 'table', label: 'Таблица', icon: 'table', iconOnly: true }, { value: 'grid', label: 'Галерея', icon: 'grid', iconOnly: true }]} />
          </Row>
        </PageBlock>

        <PageBlock title="Вкладки, стрелки, счётчики" help="Линия под вкладкой скользит к выбранной; стрелки ←/→ переключают вкладки. Не влезающие в ширину уходят в «Ещё». Счётчик меняет число мягко: больше — снизу, меньше — сверху.">
          {/* All tabs while they fit; «Ещё» only when the row is too narrow (narrow the window to see it). */}
          <Tabs label="Пример вкладок" value={tab} onChange={setTab}
            items={[{ key: 'one', label: 'Основное' }, { key: 'two', label: 'Билеты', count }, { key: 'three', label: 'История' }, { key: 'more', label: 'Платежи' }]} />
          <Row label="Chevron">
            <Button aria-expanded={open} onClick={() => setOpen(!open)}><Chevron dir="right" open={open} />Папка</Button>
            <Button onClick={() => setOpen(!open)}><Chevron open={open} />Показать ещё</Button>
            <Chevron dir="left" /><Chevron dir="up" /><Chevron dir="right" /><Chevron dir="down" />
          </Row>
          <Row label="Count и Chip">
            <Button size="sm" onClick={() => setCount(count - 1)}>−1</Button>
            <span className={s.count} data-testid="count"><Count value={count} /></span>
            <Button size="sm" onClick={() => setCount(count + 1)}>+1</Button>
            <Chip color="blue" dot>Доклад</Chip><Chip color="green">Оплачен</Chip><Chip color="red">Отказ</Chip>
          </Row>
        </PageBlock>

        <PageBlock title="Подсказка, тост, диалог" help="Подсказка — по наведению за 250 мс и сразу по фокусу с клавиатуры; тост выезжает снизу; диалог и меню уходят так же заметно, как появились.">
          <Row label="Показать">
            <Tooltip label="Подсказка справа"><Button variant="secondary">Наведите</Button></Tooltip>
            <Button variant="secondary" onClick={() => toast({ kind: 'info', text: 'Сохранено', action: { label: 'Отменить', run: () => {} } })}>Тост</Button>
            <Button variant="secondary" onClick={() => toast({ kind: 'error', text: 'Не сохранилось: нет сети' })}>Тост с ошибкой</Button>
            <Button variant="secondary" onClick={() => setDialog(true)}>Диалог</Button>
          </Row>
        </PageBlock>
      </div>
      <Dialog open={dialog} onOpenChange={setDialog} label="Пример диалога">
        <div className={s.dialog}>
          <h2 className={s.dialogTitle}>Удалить вид «Мои сделки»?</h2>
          <p className={s.label}>Вид исчезнет у всех, кто им пользуется. Записи останутся.</p>
          <div className={s.cells}><Button onClick={() => setDialog(false)}>Отмена</Button><Button variant="primary" onClick={() => setDialog(false)}>Удалить</Button></div>
        </div>
      </Dialog>
    </Page>
  );
}
